import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { useNotifications } from '../lib/notifications';
import { useQueue } from '../lib/queue';
import { OfflineBanner } from '../components/OfflineBanner';
import { InstallPrompt, UpdatePrompt } from '../components/InstallPrompt';
import { SyncSheet } from '../components/SyncSheet';
import { Bell, CalendarCheck, ListIcon, UserIcon } from '../components/icons';
import '../app.css';

/**
 * Driver app shell: header (wordmark + name + unread badge) + sticky
 * OfflineBanner + bottom tab nav (Today / Jobs / Alerts / Profile).
 * Job detail hides the bottom nav in favour of the page's action bar.
 */
export function DriverShell() {
  const { user } = useAuth();
  const location = useLocation();
  const [syncOpen, setSyncOpen] = useState(false);
  const { unread } = useNotifications();
  const { pending, failed } = useQueue();

  const isDetail = /^\/jobs\/[^/]+$/.test(location.pathname);
  const badgeCount = pending + failed;

  return (
    <div className="pwa-shell">
      <header className="pwa-header">
        <span className="pwa-header__brand">
          <img src="/logo-icon-t.png" alt="" className="pwa-header__logo" />
          <span className="u-wordmark">UR-ROUTE</span>
        </span>
        <div className="pwa-header__right">
          {badgeCount > 0 ? (
            <button
              type="button"
              className="pwa-header__badge pwa-header__badge--sync"
              onClick={() => setSyncOpen(true)}
              aria-label={`${badgeCount} changes waiting to sync`}
              data-testid="pending-sync"
            >
              <span className="u-data">{badgeCount}</span>
            </button>
          ) : null}
          <span className="pwa-header__name">{user?.name ?? 'Driver'}</span>
        </div>
      </header>

      <OfflineBanner onOpenSync={() => setSyncOpen(true)} />

      <main className={`pwa-main${isDetail ? ' pwa-main--detail' : ''}`}>
        <Outlet />
      </main>

      {!isDetail ? (
        <nav className="pwa-nav" aria-label="Driver navigation">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              isActive ? 'pwa-nav__item pwa-nav__item--active' : 'pwa-nav__item'
            }
            data-testid="nav-today"
          >
            <CalendarCheck size={20} />
            <span>Today</span>
          </NavLink>
          <NavLink
            to="/jobs"
            className={({ isActive }) =>
              isActive ? 'pwa-nav__item pwa-nav__item--active' : 'pwa-nav__item'
            }
            data-testid="nav-jobs"
          >
            <ListIcon size={20} />
            <span>Jobs</span>
          </NavLink>
          <NavLink
            to="/alerts"
            className={({ isActive }) =>
              isActive ? 'pwa-nav__item pwa-nav__item--active' : 'pwa-nav__item'
            }
            data-testid="nav-alerts"
          >
            <span className="pwa-nav__iconwrap">
              <Bell size={20} />
              {unread > 0 ? (
                <span className="pwa-nav__badge u-data" data-testid="unread-badge">
                  {unread > 99 ? '99+' : unread}
                </span>
              ) : null}
            </span>
            <span>Alerts</span>
          </NavLink>
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              isActive ? 'pwa-nav__item pwa-nav__item--active' : 'pwa-nav__item'
            }
            data-testid="nav-profile"
          >
            <UserIcon size={20} />
            <span>Profile</span>
          </NavLink>
        </nav>
      ) : null}

      <SyncSheet open={syncOpen} onClose={() => setSyncOpen(false)} />

      <InstallPrompt />
      <UpdatePrompt />
    </div>
  );
}
